import { Injectable } from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";
import { CookiePreferences, PreferencesService } from "../angular-core/services/preferences.service";
import { hasValue } from "../angular-core/utils/functions/common.functions";
import { CookieConsentDialogComponent } from "../modules/cookieconsent/cookieconsent-dialog.component";
import { DialogAnimationService } from "../angular-core/services/dialog-animation.service";
import { filter, take } from "rxjs";

declare global {
  function gtag(...args: any[]): void;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private initialized: boolean = false;

  constructor(
    private readonly preferencesService: PreferencesService,
    private readonly dialogService: DialogAnimationService,
  ) { }

  public trackPageView(title: string, location: string): void {
    if (typeof gtag !== 'undefined' && gtag) {
      gtag('event', 'page_view', {
        'page_location': location,
        'page_title': title,
      });
    }
  }

  public initialize(): void {
    if (this.initialized) {
      return;
    }

    if (typeof gtag !== 'undefined' && gtag) {
      this.preferencesService.loaded
        .pipe(
          filter(l => l), 
          take(1)
        )
        .subscribe(() => {
          const consent = this.readConsent();
          if (!hasValue(consent) || consent === CookiePreferences.NotSet) {
            this.showConsentDialog();
          }

          if (!this.hasGaCookie()
            && consent === CookiePreferences.Allow) {
            // if consent was given, but cookie not present, re-set the GA cookie
            this.setGaConsent(true);
          }

          this.preferencesService.cookieConsent.subscribe((consent) => { this.setGaConsent(consent === CookiePreferences.Allow) });
        });
      this.initialized = true;
    }
  }

  public showConsentDialog(): void {
    let dialog = this.dialogService.open(CookieConsentDialogComponent, {
      panelClass: 'messagebox-dialog',
      disableClose: true
    });

    dialog.afterClosed().subscribe((result: boolean) => {
      this.setGaConsent(result);
      this.storeConsent(result);
    });
  }

  private setGaConsent(analytics: boolean): void {
    if (typeof gtag !== 'undefined' && gtag) {
      gtag('consent', 'update', {
        'analytics_storage': analytics ? 'granted' : 'denied',
      });
    }
  }

  private storeConsent(analytics: boolean): void {
    let cookiePreferences = analytics ? CookiePreferences.Allow : CookiePreferences.Disallow;
    this.preferencesService.setCookieConsent(cookiePreferences);
  }

  private readConsent(): CookiePreferences {
    let analyticsConsent = this.preferencesService.getCookieConsent();
    return analyticsConsent;
  }

  private hasGaCookie(): boolean {
    const cookieName = '_ga';
    const nameLenPlus = (cookieName.length + 1);
    let cookie = document.cookie
      .split(';')
      .map(c => c.trim())
      .filter(cookie => {
        return cookie.substring(0, nameLenPlus) === `${cookieName}=`;
      })
      .map(cookie => {
        return decodeURIComponent(cookie.substring(nameLenPlus));
      })[0] || null;

    return cookie != null ? true : false;
  }
}